import React from 'react';
import { InlineMath } from 'react-katex';
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from '../../../../components/inputs/IntervalInput';
import TwoRangesOverlap from '../../../../components/visuals/TwoRangesOverlap';

/* === losowanie === */
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const coin = () => Math.random() < 0.5;

/* === narzędzia na przedziały === */
const byLeft = (p, q) =>
  (p.left === -Infinity ? -1 : q.left === -Infinity ? 1 : p.left - q.left);

const isEmpty = (S) => !S || !S.intervals || S.intervals.length === 0;

function sameIntervals(parsed, expect) {
  if (!parsed || parsed.kind !== 'intervals' || expect.kind !== 'intervals') return false;
  const A = (parsed.intervals || []).slice().sort(byLeft);
  const B = (expect.intervals || []).slice().sort(byLeft);
  if (A.length !== B.length) return false;
  const eq = (x, y) =>
    (x === Infinity && y === Infinity) ||
    (x === -Infinity && y === -Infinity) ||
    (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < 1e-9);
  for (let i = 0; i < A.length; i++) {
    const I = A[i], J = B[i];
    if (!(eq(I.left, J.left) && eq(I.right, J.right) &&
          I.leftClosed === J.leftClosed && I.rightClosed === J.rightClosed)) {
      return false;
    }
  }
  return true;
}

function normalizeUnion(S) {
  const arr = (S.intervals || []).slice().sort(byLeft);
  const out = [];
  for (const cur of arr) {
    if (!out.length) { out.push({ ...cur }); continue; }
    const last = out[out.length - 1];
    const overlap =
      last.right > cur.left ||
      (last.right === cur.left && (last.rightClosed || cur.leftClosed));
    if (overlap) {
      if (
        cur.right > last.right ||
        (cur.right === last.right && cur.rightClosed && !last.rightClosed)
      ) {
        last.right = cur.right;
        last.rightClosed = cur.rightClosed;
      }
    } else {
      out.push({ ...cur });
    }
  }
  return { kind: 'intervals', intervals: out };
}

function intersect2(I, J) {
  const left = Math.max(I.left, J.left);
  const right = Math.min(I.right, J.right);

  let leftClosed;
  if (I.left > J.left) leftClosed = I.leftClosed;
  else if (I.left < J.left) leftClosed = J.leftClosed;
  else leftClosed = I.leftClosed && J.leftClosed;

  let rightClosed;
  if (I.right < J.right) rightClosed = I.rightClosed;
  else if (I.right > J.right) rightClosed = J.rightClosed;
  else rightClosed = I.rightClosed && J.rightClosed;

  if (left < right) return { left, right, leftClosed, rightClosed };
  if (left === right && leftClosed && rightClosed)
    return { left, right, leftClosed: true, rightClosed: true };
  return null;
}

function subtract2(I, J) {
  const K = intersect2(I, J);
  if (!K) return [I];

  const parts = [];
  if (I.left < K.left || (I.left === K.left && I.leftClosed && !K.leftClosed)) {
    parts.push({
      left: I.left,
      right: K.left,
      leftClosed: I.leftClosed,
      rightClosed: !K.leftClosed,
    });
  }
  if (K.right < I.right || (K.right === I.right && !K.rightClosed && I.rightClosed)) {
    parts.push({
      left: K.right,
      right: I.right,
      leftClosed: !K.rightClosed,
      rightClosed: I.rightClosed,
    });
  }
  return parts;
}

function difference(A, B) {
  let current = (A.intervals || []).map(I => ({ ...I }));
  for (const J of B.intervals) {
    const next = [];
    for (const I of current) next.push(...subtract2(I, J));
    current = next;
  }
  return normalizeUnion({ kind: 'intervals', intervals: current });
}

function union(A, B) {
  return normalizeUnion({
    kind: 'intervals',
    intervals: [...(A.intervals || []), ...(B.intervals || [])],
  });
}

/* === prezentacja === */
function setToLatex(S) {
  if (isEmpty(S)) return '\\varnothing';
  const parts = S.intervals.map(I => {
    if (I.left === I.right && I.leftClosed && I.rightClosed) return `\\{${I.left}\\}`;
    if (I.left === -Infinity) return `(-\\infty, ${I.right}${I.rightClosed ? ']' : ')'}`;
    if (I.right === Infinity) return `${I.leftClosed ? '[' : '('}${I.left}, \\infty)`;
    return `${I.leftClosed ? '[' : '('}${I.left}, ${I.right}${I.rightClosed ? ']' : ')'}`;
  });
  return parts.join(' \\cup ');
}

function toRaw(S) {
  return S.intervals.map(I =>
    I.left === I.right && I.leftClosed && I.rightClosed ? `\\{${I.left}\\}` :
    I.left === -Infinity ? `(-∞, ${I.right}${I.rightClosed ? ']' : ')'}` :
    I.right === Infinity ? `${I.leftClosed ? '[' : '('}${I.left}, ∞)` :
    `${I.leftClosed ? '[' : '('}${I.left}, ${I.right}${I.rightClosed ? ']' : ')'}`,
  ).join(' ∪ ');
}

function labeledRanges(S) {
  return S.intervals.map(I => ({
    from: I.left,
    to: I.right,
    leftClosed: I.leftClosed,
    rightClosed: I.rightClosed,
    leftLabelLatex: I.left !== -Infinity ? `${I.left}` : undefined,
    rightLabelLatex: I.right !== Infinity ? `${I.right}` : undefined,
  }));
}

/* === generator A (z wagami 1/5 dla każdego typu) === */
function singleFinite() {
  const a = randInt(-6, 2);
  const b = randInt(a + 2, a + 8);
  return {
    kind: 'intervals',
    intervals: [{ left: a, right: b, leftClosed: coin(), rightClosed: coin() }],
  };
}

function twoFiniteDisjoint() {
  const a = randInt(-7, 0);
  const b = randInt(a + 1, a + 4);
  const c = randInt(b + 1, b + 4);
  const d = randInt(c + 1, c + 6);
  return {
    kind: 'intervals',
    intervals: [
      { left: a, right: b, leftClosed: coin(), rightClosed: coin() },
      { left: c, right: d, leftClosed: coin(), rightClosed: coin() },
    ],
  };
}

function semiInfiniteOne() {
  const k = randInt(-3, 6);
  const right = coin();
  const closed = coin();
  return {
    kind: 'intervals',
    intervals: right
      ? [{ left: k, right: Infinity, leftClosed: closed, rightClosed: false }]
      : [{ left: -Infinity, right: k, leftClosed: false, rightClosed: closed }],
  };
}

function twoWithInfinities() {
  // (-∞, a] ∪ [b, ∞) (z losowymi domknięciami) – i tak jest rozłącznie
  const a = randInt(-3, 2);
  const b = randInt(a + 2, a + 7);
  const lc = coin(), rc = coin();
  return {
    kind: 'intervals',
    intervals: [
      { left: -Infinity, right: a, leftClosed: false, rightClosed: rc },
      { left: b, right: Infinity, leftClosed: lc, rightClosed: false },
    ],
  };
}

function twoSemiOrMixed() {
  // Rozłącznie: półprosta po jednej stronie, odcinek w całości po drugiej.
  const a = randInt(-4, 4);
  const right = coin();           // true => (a, ∞), false => (-∞, a)
  const edgeClosed = coin();

  let inf, fin;

  if (right) {
    // półprosta w prawo od a
    inf = { left: a, right: Infinity, leftClosed: edgeClosed, rightClosed: false };
    // odcinek całkiem po LEWEJ: d ≤ a-1
    const d = randInt(a - 5, a - 1);
    const c = randInt(d - 5, d - 1);
    fin = { left: c, right: d, leftClosed: coin(), rightClosed: coin() };
  } else {
    // półprosta w lewo do a
    inf = { left: -Infinity, right: a, leftClosed: false, rightClosed: edgeClosed };
    // odcinek całkiem po PRAWEJ: c ≥ a+1
    const c = randInt(a + 1, a + 6);
    const d = randInt(c + 1, c + 6);
    fin = { left: c, right: d, leftClosed: coin(), rightClosed: coin() };
  }

  return { kind: 'intervals', intervals: [inf, fin] };
}

function genA() {
  const r = Math.random();
  if (r < 0.20) return singleFinite();
  if (r < 0.40) return twoFiniteDisjoint();
  if (r < 0.60) return semiInfiniteOne();
  if (r < 0.80) return twoWithInfinities();
  return twoSemiOrMixed();
}

/* === wszechświat i dopełnienie === */
const R = { kind: 'intervals', intervals: [
  { left: -Infinity, right: Infinity, leftClosed: false, rightClosed: false }
]};

function complementInReals(A) {
  return difference(R, A);
}

/* === eksport generatora === */
export const generateTask = () => {
  const A0 = genA();
  // bezpieczeństwo: zawsze ładny, rozłączny zapis
  const A = normalizeUnion(A0);
  return { type: 'RealsMinusATask', data: { A } };
};

/* === polecenie === */
export function renderTask({ data }) {
  const { A } = data;
  return (
    <div style={{ fontSize: 16, lineHeight: 1.5, marginTop: 10, marginBottom: 10 }}>
      Wyznacz zbiór <InlineMath math={'\\mathbb{R}\\setminus A'} />, dla{' '}
      <InlineMath math={`A=${intervalToLatex(toRaw(A))}`} />.
    </div>
  );
}

/* === input === */
export function renderInput(t, u, onChange, dis) {
  const ans = u || {};
  return (
    <div className="space-y-2">
      <div><b>Odpowiedź:</b> <InlineMath math={'x\\in'} /></div>
      <IntervalInput
        value={ans?.result}
        onChange={v => onChange({ ...ans, result: v })}
        disabled={dis}
        asString
        placeholder={'np. (-∞, -2] ∪ [0,3] ∪ (5, ∞)'}
        previewWithX
      />
    </div>
  );
}

/* === sprawdzanie === */
export function checkAnswer({ data }, user) {
  const normalizeParsed = (p) => {
    if (!p) return null;
    if (p.kind === 'empty') return { kind: 'intervals', intervals: [] };
    if (p.kind === 'all')   return { kind: 'intervals', intervals: [
      { left: -Infinity, right: Infinity, leftClosed: false, rightClosed: false }
    ]};
    return p;
  };
  const parseOne = (x) => {
    const p = (typeof x === 'string' ? parseIntervalInput(x) : x?.parsed);
    return normalizeParsed(p);
  };

  const { A } = data;
  const expect = complementInReals(A);
  const got = parseOne(user?.result);
  const isOk = sameIntervals(got, expect);

  // Bez lokalnej punktacji – QuizRunner policzy globalnie.
  return { isCorrect: isOk };
}

/* === odpowiedź wzorcowa === */
export function renderAnswer({ data }) {
  const { A } = data;
  const C = complementInReals(A);
  return (
    <div>
      <InlineMath math={`x\\in\\;${setToLatex(C)}`} />
    </div>
  );
}

/* === wyjaśnienie === */
export function renderExplanation({ data }) {
  const { A } = data;
  const C = complementInReals(A);

  return (
    <div className="space-y-2">
      <div>
        <b>Krok 1:</b>{' '}
        <InlineMath math={'\\mathbb{R}\\setminus A'} /> to „wszystko poza”
        {' '}<InlineMath math={'A'} /> w zbiorze <InlineMath math={'\\mathbb{R}'} />.
      </div>

      <div>
        <b>Krok 2:</b> Na jednej osi rysujemy: zadany{' '}
        <InlineMath math={'A'} /> – niebiesko (niżej) oraz wynik{' '}
        <InlineMath math={'\\mathbb{R}\\setminus A'} /> – pomarańczowo (wyżej).
      </div>

      <div style={{ maxWidth: 720, margin: '6px auto 10px' }}>
        <TwoRangesOverlap
          showZero
          lockPair
          pairFractions={[0.45, 0.55]}
          overlapColorPreset="pink"
          overlapOpacity={0.25}
          overlapBlur={1.25}
          ranges={[labeledRanges(C), labeledRanges(A)]}
        />
      </div>

      <div>
        <b>Krok 3:</b>{' '}
        <InlineMath math={'x\\in\\;'+setToLatex(C)} />
      </div>

      <div style={{opacity: 0.8}}>
        <small>
          (Dla porównania, zadany <InlineMath math={'A'} /> to{' '}
          <InlineMath math={intervalToLatex(toRaw(A))} />.)
        </small>
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const raw = typeof userAnswer.result === 'string'
    ? userAnswer.result
    : userAnswer.result?.raw;
  if (!raw) return (<i>(brak)</i>);
  return (
    <div>
      <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />
    </div>
  );
}
